/**
 * Keyboard shortcut keys for transaction screens — the screen's own (system)
 * keys and the keys a designer gives an action button (`ActionDef.shortcut`)
 * or a Line Entry command (`LineCommand.shortcut`).
 *
 * ONE source of the rules: `actionShortcuts.json` (system keys, keys the
 * browser / OS / text editing own, the key grammar). The engine's
 * `twasta_runtime_engine/action_shortcuts.py` reads the SAME file and gives
 * the same answer for every case in `test-script/action_shortcut_fixtures/`
 * — a rule change is the JSON (or both parsers) + the fixtures.
 *
 * A system key can never be taken by a custom button: the designer refuses
 * it, the lint / save refuse it, and here `actionShortcutCombo` ignores it
 * so a hand-edited model still cannot steal F3 from Add.
 *
 * Doc: docs/action_shortcut_keys.md.
 */
import SHORTCUT_CONFIG from './actionShortcuts.json'
import type { Action, ActionDef } from './types'
import { legacyActionToDef, isMergedSystemAction, isExportSystemAction } from './types'

export type SystemShortcutOp =
  | 'add' | 'edit' | 'delete' | 'save' | 'save_close' | 'close'
  | 'lookup' | 'expand' | 'next_page' | 'previous_page' | 'search'

export interface SystemShortcut {
  op: SystemShortcutOp
  combo: string
  label: string
  where: 'list' | 'editor'
  /** The system action buttons (`system_op`) whose fixed key this is. */
  systemOps?: string[]
}

interface ShortcutConfig {
  modifiers: string[]
  modifierAliases: Record<string, string>
  keyAliases: Record<string, string>
  bareKeys: string[]
  namedKeys: string[]
  characterKeys: string
  system: SystemShortcut[]
  reserved: Array<{ combo: string; reason: string }>
}

const CFG = SHORTCUT_CONFIG as unknown as ShortcutConfig

/** Why a typed shortcut cannot be used — the same codes as the engine. */
export type ShortcutProblemCode =
  | 'unknown_key'      // a part that is neither a modifier nor a known key
  | 'no_key'           // only modifiers ("Ctrl+")
  | 'too_many_keys'    // two non-modifier keys ("Ctrl+A+B")
  | 'needs_modifier'   // a letter / named key with no Ctrl or Alt
  | 'system'           // one of the screen's own keys (F3 Add, Ctrl+S Save …)
  | 'reserved'         // the browser / OS / text editing owns it

export interface ShortcutParse {
  /** Canonical combo (`ctrl+alt+shift+<key>`), or null when empty / invalid. */
  combo: string | null
  /** How to show it ("Ctrl+Shift+K", "F7"). */
  display: string
  problem?: { code: ShortcutProblemCode; message: string; op?: SystemShortcutOp }
}

const MODS = CFG.modifiers
const SYSTEM_BY_COMBO = new Map(CFG.system.map(s => [s.combo, s]))
const SYSTEM_BY_OP = new Map(CFG.system.map(s => [s.op, s]))
const RESERVED = new Map(CFG.reserved.map(r => [r.combo, r.reason]))
const KNOWN_KEYS = new Set<string>([
  ...CFG.bareKeys, ...CFG.namedKeys, ...Array.from(CFG.characterKeys),
])

/** The system shortcuts, in the order the JSON lists them. */
export function systemShortcuts(): SystemShortcut[] {
  return CFG.system.map(s => ({ ...s }))
}

/** The combo of a system key (`'f3'` for add). Never write these as literals. */
export function systemCombo(op: SystemShortcutOp): string {
  const s = SYSTEM_BY_OP.get(op)
  if (!s) throw new Error(`actionShortcuts.json has no system key "${op}"`)
  return s.combo
}

/** The fixed key of a SYSTEM action button (`handler: 'system'`), or null
 *  (export, or not a system button). */
export function systemShortcutForAction(a: Action | ActionDef | null | undefined): SystemShortcut | null {
  if (!a) return null
  const def = legacyActionToDef(a as Action)
  if (def.handler !== 'system') return null
  const op = def.system_op || 'add'
  return CFG.system.find(s => (s.systemOps || []).includes(op)) || null
}

/** The system key a combo belongs to, if any. */
export function systemShortcutOf(combo: string | null | undefined): SystemShortcut | null {
  return (combo && SYSTEM_BY_COMBO.get(combo)) || null
}

function isMacPlatform(): boolean {
  try {
    const nav = typeof navigator !== 'undefined' ? navigator : null
    const p = (nav as unknown as { userAgentData?: { platform?: string } } | null)?.userAgentData?.platform
      || nav?.platform || ''
    return /mac|iphone|ipad/i.test(p)
  } catch {
    return false
  }
}

const KEY_LABEL: Record<string, string> = {
  pageup: 'PageUp', pagedown: 'PageDown', arrowup: '↑', arrowdown: '↓',
  arrowleft: '←', arrowright: '→', escape: 'Esc', delete: 'Delete',
  insert: 'Insert', home: 'Home', end: 'End', enter: 'Enter', space: 'Space',
  backspace: 'Backspace', tab: 'Tab',
}

/** "ctrl+shift+k" → "Ctrl+Shift+K" ("⌘+Shift+K" on a Mac when `mac`). */
export function displayCombo(combo: string | null | undefined, opts: { mac?: boolean } = {}): string {
  if (!combo) return ''
  const mac = opts.mac ?? false
  // No key is '+' (Shift+= is written '='), so '+' only ever separates.
  const parts = combo.split('+')
  return parts.map((p, i) => {
    if (i < parts.length - 1) {
      if (p === 'ctrl') return mac ? '⌘' : 'Ctrl'
      if (p === 'alt') return mac ? '⌥' : 'Alt'
      if (p === 'shift') return 'Shift'
    }
    return KEY_LABEL[p] ?? p.toUpperCase()
  }).join('+')
}

/** The hint shown beside a button / menu item on THIS device. */
export function shortcutHint(combo: string | null | undefined): string {
  return displayCombo(combo, { mac: isMacPlatform() })
}

function problem(code: ShortcutProblemCode, message: string, op?: SystemShortcutOp): ShortcutParse['problem'] {
  return op ? { code, message, op } : { code, message }
}

/**
 * Typed text ("Ctrl+Shift+K", "alt r", "⌘S", "F7") → canonical combo, or the
 * reason it cannot be a custom shortcut. Empty text is no shortcut (no
 * problem).
 */
export function parseShortcut(text: string | null | undefined): ShortcutParse {
  const raw = String(text ?? '').trim().toLowerCase()
  if (!raw) return { combo: null, display: '' }
  // "⌘S" / "⌥R" — a symbol glued to its key.
  const spaced = raw.replace(/([⌘⌥⇧])(?=\S)/g, '$1+')
  const tokens = spaced.split(/[\s+]+/).filter(Boolean)
  const mods = new Set<string>()
  const keys: string[] = []
  for (const t of tokens) {
    const m = CFG.modifierAliases[t] ?? (MODS.includes(t) ? t : null)
    if (m) { mods.add(m); continue }
    const k = CFG.keyAliases[t] ?? t
    if (!KNOWN_KEYS.has(k)) {
      return { combo: null, display: text || '', problem: problem('unknown_key', `"${t}" is not a key a shortcut can use.`) }
    }
    keys.push(k)
  }
  if (keys.length === 0) {
    return { combo: null, display: text || '', problem: problem('no_key', 'Add a key after the modifier (for example Ctrl+Shift+K).') }
  }
  if (keys.length > 1) {
    return { combo: null, display: text || '', problem: problem('too_many_keys', 'A shortcut is one key, with Ctrl / Alt / Shift if wanted.') }
  }
  const key = keys[0]
  const combo = [...MODS.filter(m => mods.has(m)), key].join('+')
  const display = displayCombo(combo)
  const sys = SYSTEM_BY_COMBO.get(combo)
  if (sys) {
    return { combo: null, display, problem: problem('system',
      `${display} is the system key for ${sys.label} — choose another key.`, sys.op) }
  }
  const reason = RESERVED.get(combo)
  if (reason) {
    return { combo: null, display, problem: problem('reserved',
      `${display} ${reason} — choose another key.`) }
  }
  if (!CFG.bareKeys.includes(key) && !mods.has('ctrl') && !mods.has('alt')) {
    return { combo: null, display, problem: problem('needs_modifier',
      `${display} would fire while typing — use a function key (F7, F8, F9) or add Ctrl or Alt.`) }
  }
  return { combo, display }
}

/** A key press → the text the designer's key-capture box records ("Ctrl+K"),
 *  or null while only modifiers are held. Uses the same mapping as the
 *  runtime's matcher (`comboOf`). */
export function displayForEvent(e: KeyboardEvent | { key: string; code?: string; ctrlKey: boolean; metaKey: boolean; altKey: boolean; shiftKey: boolean }): string | null {
  const key = keyOfEvent(e)
  if (!key || key === 'control' || key === 'meta' || key === 'alt' || key === 'shift'
      || key === 'os' || key === 'altgraph') return null
  const parts: string[] = []
  if (e.ctrlKey || e.metaKey) parts.push('ctrl')
  if (e.altKey) parts.push('alt')
  if (e.shiftKey) parts.push('shift')
  parts.push(key)
  return displayCombo(parts.join('+'))
}

/**
 * The key a keyboard event names, lower-case. `e.key` normally — it follows
 * the person's keyboard layout — but a modifier can change the character
 * (Option+R is "®" on a Mac, Shift+1 is "!"), so a printable key outside the
 * shortcut alphabet falls back to the PHYSICAL key (`e.code` KeyR / Digit1).
 */
export function keyOfEvent(e: { key: string; code?: string }): string {
  let key = (e.key || '').toLowerCase()
  if (key === ' ' || key === 'spacebar') key = 'space'
  if (key.length === 1 && !CFG.characterKeys.includes(key)) {
    const m = /^(?:Key([A-Z])|Digit([0-9]))$/.exec(e.code || '')
    if (m) key = (m[1] || m[2]).toLowerCase()
  }
  return key
}

/**
 * The combo a CUSTOM action button answers to at runtime, or null: no key,
 * an invalid one, a system / reserved one, a system action (its keys are the
 * screen's own and fixed), or a disabled button.
 */
export function actionShortcutCombo(a: Action | ActionDef | null | undefined): string | null {
  if (!a) return null
  const def = legacyActionToDef(a as Action)
  if (def.disabled || !(def.name || '').trim()) return null
  if (def.handler === 'system' || isMergedSystemAction(def) || isExportSystemAction(def)) return null
  return parseShortcut(def.shortcut).combo
}

/**
 * Custom action buttons → `{combo, name}` for the keyboard layer, the FIRST
 * button (in the designer's order) winning a key two buttons share — the
 * lint reports the clash.
 */
export function actionShortcutBindings(actions: Array<Action | ActionDef>): Array<{ combo: string; name: string }> {
  const seen = new Set<string>()
  const out: Array<{ combo: string; name: string }> = []
  for (const a of actions) {
    const combo = actionShortcutCombo(a)
    if (!combo || seen.has(combo)) continue
    seen.add(combo)
    out.push({ combo, name: legacyActionToDef(a as Action).name })
  }
  return out
}

/** The text after a label in a title / tooltip: "Approve (Alt+A)". */
export function withShortcutHint(label: string, combo: string | null | undefined): string {
  const h = shortcutHint(combo)
  return h ? `${label} (${h})` : label
}

/**
 * Problems with the shortcut keys of a whole set of buttons (one object's
 * actions, or a line form's commands): each entry's own problem plus a key
 * used twice. Keyed by the entry's index. The designer shows these inline;
 * the engine's `model_problems` reports the same.
 */
export function shortcutClashes(entries: Array<{ name: string; shortcut?: string | null }>): Record<number, string> {
  const out: Record<number, string> = {}
  const first = new Map<string, string>()
  entries.forEach((e, i) => {
    const p = parseShortcut(e.shortcut)
    if (p.problem) { out[i] = p.problem.message; return }
    if (!p.combo) return
    const prev = first.get(p.combo)
    if (prev !== undefined) out[i] = `${p.display} is already the key for "${prev}".`
    else first.set(p.combo, e.name || '(unnamed)')
  })
  return out
}

/**
 * Every shortcut problem of a transaction (the `transaction` object), as
 * plain sentences — the client twin of the engine's
 * `action_shortcuts.model_problems`, run by the designer before it saves:
 * an unusable key, a key on a SYSTEM button, a key two buttons of the object
 * share (object + every form's buttons share one keyboard), and a key two
 * Line Entry commands of one line form share.
 */
export function modelShortcutProblems(txn: unknown): string[] {
  const t = (txn && typeof txn === 'object' ? txn : {}) as {
    actions?: unknown[]
    sql_models?: Array<{ sql_model?: unknown } | Record<string, unknown>>
  }
  const forms = (t.sql_models || []).map((item, i) => {
    const sm = ((item as { sql_model?: unknown }).sql_model ?? item) as {
      form_name?: string; form_no?: string; actions?: unknown[]
      line_entry?: { commands?: Array<{ name?: string; shortcut?: string }> }
    }
    return { ref: String(sm?.form_name || sm?.form_no || `form #${i + 1}`), sm: sm || {} }
  })
  const entries: Array<{ where: string; a: Record<string, unknown> }> = [
    ...(t.actions || []).filter(a => a && typeof a === 'object').map(a => ({ where: 'object', a: a as Record<string, unknown> })),
    ...forms.flatMap(f => (f.sm.actions || []).filter(a => a && typeof a === 'object')
      .map(a => ({ where: `form '${f.ref}'`, a: a as Record<string, unknown> }))),
  ]
  const out: string[] = []
  const owner = new Map<string, string>()
  for (const { where, a } of entries) {
    const raw = a.shortcut
    if (raw == null || String(raw).trim() === '') continue
    const name = String(a.name || '').trim() || '(unnamed)'
    const label = `Button '${name}' (${where})`
    if (a.handler === 'system') {
      out.push(`${label}: a system button's key is the screen's own and fixed — remove its shortcut.`)
      continue
    }
    const p = parseShortcut(String(raw))
    if (p.problem || !p.combo) { out.push(`${label}: ${p.problem?.message || 'unusable key'}`); continue }
    const prev = owner.get(p.combo)
    if (prev !== undefined) out.push(`${label}: ${p.display} is already the key for '${prev}'.`)
    else owner.set(p.combo, name)
  }
  for (const f of forms) {
    const seen = new Map<string, string>()
    for (const c of f.sm.line_entry?.commands || []) {
      if (!c || c.shortcut == null || String(c.shortcut).trim() === '') continue
      const name = String(c.name || '').trim() || '(unnamed)'
      const label = `Line command '${name}' (form '${f.ref}')`
      const p = parseShortcut(c.shortcut)
      if (p.problem || !p.combo) { out.push(`${label}: ${p.problem?.message || 'unusable key'}`); continue }
      const prev = seen.get(p.combo)
      if (prev !== undefined) out.push(`${label}: ${p.display} is already the key for '${prev}'.`)
      else seen.set(p.combo, name)
    }
  }
  return out
}
